// src/components/Header.js
import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useNavigate, useLocation } from 'react-router-dom';
import DownloadButton from './DownloadButton';
import { azureColors } from '../theme';
import brandIcon from '../assets/profile.jpg';

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const navItems = [
    { label: 'Home', path: '/' },
    { label: 'Products', path: '/products' },
    { label: 'Services', path: '/services' },
    { label: 'Beyond Work', path: '/beyond-work' },
    { label: 'Contact', path: '/contact' }
  ];

  return (
    <>
      {/* Azure accent strip */}
      <Box
        sx={{
          height: 4,
          background: `linear-gradient(90deg, ${azureColors.navy} 0%, ${azureColors.primary} 45%, ${azureColors.accent} 100%)`,
        }}
      />
      <AppBar position="sticky" elevation={0} color="inherit">
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, gap: 2 }}>
          <Box
            onClick={() => handleNavigation('/')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              mr: { sm: 2 },
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={brandIcon}
              alt="Mario Bozhinovski"
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, lineHeight: 1.1, color: azureColors.text }}
              >
                Mario Bozhinovski
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: azureColors.textSecondary, lineHeight: 1 }}
              >
                Aldea Systems
              </Typography>
            </Box>
          </Box>

          {/* Desktop Navigation */}
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexGrow: 1, gap: 0.5, alignItems: 'center' }}>
            {navItems.map((item) => {
              const active = location.pathname === item.path;
              return (
                <Box
                  key={item.path}
                  component="button"
                  type="button"
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    fontFamily: 'inherit',
                    fontSize: '0.9375rem',
                    color: active ? azureColors.primary : azureColors.text,
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '10px 14px',
                    borderBottom: active
                      ? `2px solid ${azureColors.primary}`
                      : '2px solid transparent',
                    transition: 'color 0.15s ease, border-color 0.15s ease, background-color 0.15s ease',
                    fontWeight: active ? 600 : 400,
                    borderRadius: '2px 2px 0 0',
                    '&:hover': {
                      color: azureColors.primary,
                      backgroundColor: 'rgba(0, 120, 212, 0.06)',
                      borderBottomColor: azureColors.primary,
                    }
                  }}
                >
                  {item.label}
                </Box>
              );
            })}
            <Box sx={{ marginLeft: 'auto' }}>
              <DownloadButton />
            </Box>
          </Box>

          {/* Mobile Menu Icon */}
          <IconButton
            edge="end"
            sx={{ display: { xs: 'inline-flex', sm: 'none' }, ml: 'auto', color: azureColors.text }}
            onClick={handleDrawerToggle}
            aria-label="Open navigation"
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        PaperProps={{
          sx: { borderLeft: `1px solid ${azureColors.border}` },
        }}
      >
        <List sx={{ width: 280, paddingTop: 2 }}>
          {navItems.map((item) => {
            const active = location.pathname === item.path;
            return (
              <ListItem key={item.label} disablePadding>
                <ListItemButton
                  onClick={() => handleNavigation(item.path)}
                  selected={active}
                  sx={{
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(0, 120, 212, 0.08)',
                      borderLeft: `3px solid ${azureColors.primary}`,
                    },
                  }}
                >
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontWeight: active ? 600 : 400,
                      color: active ? azureColors.primary : azureColors.text,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
          <ListItem sx={{ justifyContent: 'center', paddingY: 2 }}>
            <DownloadButton />
          </ListItem>
        </List>
      </Drawer>
    </>
  );
};

export default Header;
